import { AxiosError, AxiosResponse } from "axios";
import { all, call, put, takeLatest } from "redux-saga/effects";
import {
  fetchBillingCreateSuccess,
  fetchBillingCreateFailure,
  fetchBillingConfirmSuccess,
  fetchBillingConfirmFailure,
  fetchBillingAuthorizationSuccess,
  fetchBillingAuthorizationFailure,
  fetchBillingCustomerInfoSuccess,
  fetchBillingCustomerInfoFailure,
  fetchBillingInfoSuccess,
  fetchBillingInfoFailure,
  fetchBillingMakeFeeSuccess,
  fetchBillingMakeFeeFailure,
  fetchBillingPackageFailure,
  fetchBillingPackageSuccess,
} from "./actions";
import {
  FETCH_BILLING_CREATE_REQUEST,
  FETCH_BILLING_CONFIRM_REQUEST,
  FETCH_BILLING_AUTHORIZATION_KEY_REQUEST,
  FETCH_BILLING_CUSTOMER_INFO_REQUEST,
  FETCH_BILLING_INFO_REQUEST,
  FETCH_BILLING_MAKE_FEE_REQUEST,
  FETCH_BILLING_PACKAGE_REQUEST,
} from "./actions.type";

import {
  fetchBillingCreateService,
  fetchBillingConfirmService,
  fetchBillingAuthService,
  fetchBillingInfoService,
  fetchBillingPackageService,
} from "../services/billing.service";
import {
  FetchBillingCustomerInfoRequest,
  FetchBillingAuthorizationRequest,
  FetchBillingConfirmRequest,
  FetchBillingCreateRequest,
  FetchBillingMakeFeeRequest,
  FetchBillingPackageRequest,
} from "./types";
import { RESPONSEBASE } from "app/shared/services/services-model.type";
import { getErrorEndOTP, getErrorMessage } from "app/shared/services/axios";
import { hideModal, showModal } from "app/modules/modal-management/reducer/action";

function* fetchBillingInfoSaga() {
  try {
    yield put(showModal({ modalProps: { open: true }, modalType: "loading" }));
    const response: AxiosResponse<RESPONSEBASE> = yield call(fetchBillingInfoService);
    console.log("fetchBillingInfo response: ", response);
    yield put(hideModal());
    if (response.status === 200) {
      yield put(fetchBillingInfoSuccess(response.data.RESPONSEDATA));
    }
  } catch (e: any | AxiosError) {
    yield put(hideModal());
    const msg = getErrorMessage(e, true);
    yield put(fetchBillingInfoFailure(msg));
  }
}

function* fetchBillingPackageSaga(action: FetchBillingPackageRequest) {
  try {
    yield put(showModal({ modalProps: { open: true }, modalType: "loading" }));
    const response: AxiosResponse<RESPONSEBASE> = yield call(() => fetchBillingPackageService(action));
    console.log("fetchBillingPackage response: ", response);
    yield put(hideModal());
    if (response.status === 200) {
      yield put(fetchBillingPackageSuccess(response.data.RESPONSEDATA));
    }
  } catch (e: any | AxiosError) {
    yield put(hideModal());
    const msg = getErrorMessage(e, true);
    yield put(fetchBillingPackageFailure(msg));
  }
}

function* fetchBillingMakeFeeSaga(action: FetchBillingMakeFeeRequest) {
  try {
    yield put(showModal({ modalProps: { open: true }, modalType: "loading" }));
    const response: AxiosResponse<RESPONSEBASE> = yield call(() => fetchBillingCreateService(action.payload));
    console.log("fetchBillingMakeFee response: ", response);
    yield put(hideModal());
    if (response.status === 200) {
      yield put(fetchBillingMakeFeeSuccess(response.data.RESPONSEDATA));
    }
  } catch (e: any | AxiosError) {
    yield put(hideModal());
    const msg = getErrorMessage(e, true);
    yield put(fetchBillingMakeFeeFailure(msg));
  }
}

function* fetchBillingCustomerInfoSaga(action: FetchBillingCustomerInfoRequest) {
  try {
    yield put(showModal({ modalProps: { open: true }, modalType: "loading" }));
    const response: AxiosResponse<RESPONSEBASE> = yield call(() => fetchBillingCreateService(action.payload));
    console.log("fetchBillingCustomerInfo response: ", response);
    yield put(hideModal());
    if (response.status === 200) {
      const customerInfo = response.data.RESPONSEDATA?.EBINQUIRY;
      yield put(fetchBillingCustomerInfoSuccess(customerInfo));
    }
  } catch (e: any | AxiosError) {
    yield put(hideModal());
    const msg = getErrorMessage(e, true);
    yield put(fetchBillingCustomerInfoFailure(msg));
  }
}

function* fetchBillingCreateSaga(action: FetchBillingCreateRequest) {
  try {
    yield put(showModal({ modalProps: { open: true }, modalType: "loading" }));
    const response: AxiosResponse<RESPONSEBASE> = yield call(() => fetchBillingCreateService(action.payload));
    console.log("fetchBillingCreate response: ", response);
    yield put(hideModal());
    if (response.status === 200) {
      let responseData = response.data.RESPONSEDATA;
      yield put(fetchBillingCreateSuccess(responseData));
    }
  } catch (e: any | AxiosError) {
    yield put(hideModal());
    const msg = getErrorMessage(e, true);
    yield put(fetchBillingCreateFailure(msg));
  }
}

function* fetchBillingConfirmSaga(action: FetchBillingConfirmRequest) {
  try {
    yield put(showModal({ modalProps: { open: true }, modalType: "loading" }));
    const response: AxiosResponse<RESPONSEBASE> = yield call(() => fetchBillingConfirmService(action));
    console.log("fetchBillingConfirm response: ", response);
    yield put(hideModal());
    if (response.status === 200) {
      let responseData = response.data.RESPONSEDATA;
      yield put(fetchBillingConfirmSuccess(responseData));
    }
  } catch (e: any | AxiosError) {
    yield put(hideModal());
    const msg = getErrorMessage(e, true);
    yield put(fetchBillingConfirmFailure(msg));
  }
}

function* fetchBillingAuthSaga(action: FetchBillingAuthorizationRequest) {
  try {
    yield put(showModal({ modalProps: { open: true }, modalType: "loading" }));
    const response: AxiosResponse<RESPONSEBASE> = yield call(() => fetchBillingAuthService(action));
    console.log("fetchBillingAuth response: ", response);
    yield put(hideModal());
    if (response.status === 200) {
      let responseData = response.data.RESPONSEDATA;
      yield put(fetchBillingAuthorizationSuccess(responseData));
    }
  } catch (e: any | AxiosError) {
    yield put(hideModal());
    const msg = getErrorMessage(e, true);
    yield put(fetchBillingAuthorizationFailure(getErrorEndOTP(e), msg));
  }
}

function* billingSaga() {
  yield all([
    takeLatest(FETCH_BILLING_INFO_REQUEST, fetchBillingInfoSaga),
    takeLatest(FETCH_BILLING_PACKAGE_REQUEST, fetchBillingPackageSaga),
    takeLatest(FETCH_BILLING_CUSTOMER_INFO_REQUEST, fetchBillingCustomerInfoSaga),
    takeLatest(FETCH_BILLING_MAKE_FEE_REQUEST, fetchBillingMakeFeeSaga),
    takeLatest(FETCH_BILLING_CREATE_REQUEST, fetchBillingCreateSaga),
    takeLatest(FETCH_BILLING_CONFIRM_REQUEST, fetchBillingConfirmSaga),
    takeLatest(FETCH_BILLING_AUTHORIZATION_KEY_REQUEST, fetchBillingAuthSaga),
  ]);
}

export default billingSaga;
